import { useEffect, useState } from 'react';
import { api } from '../api';
import type { ColumnKey, CommentItem } from '../types';

type Props = {
  responseId: string;
  columnKey: ColumnKey;
  memberName: string;
  onCountChange?: (count: number) => void;
};

export function Comments({ responseId, columnKey, memberName, onCountChange }: Props) {
  const [items, setItems] = useState<CommentItem[]>([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.comments.list(responseId).then(all => {
      const filtered = all.filter(c => c.columnKey === columnKey);
      setItems(filtered);
      onCountChange?.(filtered.length);
    });
  };

  useEffect(load, [responseId, columnKey]);

  const post = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.comments.create({ responseId, columnKey, memberName, body });
      setBody('');
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-2 mb-3">
        {items.map(c => (
          <div key={c.id} className="border border-coast-hairline p-3">
            <div className="text-[11px] text-coast-blue uppercase tracking-widest mb-1">
              {c.memberName}
              <span className="text-coast-muted ml-2">
                {new Date(c.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            <div className="text-[15px] whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-sm text-coast-muted py-6 text-center">
            No comments yet — be first.
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-coast-hairline flex gap-2 items-start"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <textarea
          className="textarea flex-1"
          rows={2}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={`Reply as ${memberName}…`}
          enterKeyHint="send"
        />
        <button className="btn btn-primary" onClick={post} disabled={busy || !body.trim()}>
          Post
        </button>
      </div>
    </div>
  );
}
